import type { FC, ReactNode } from 'react'

import { delay } from '@/lib/ui'
import { Plate } from '@/components/generals/plate'
import { SplitWords } from '@/components/generals/split-words'
import { AboutReel } from '@/components/about/about-reel'
import { ABOUT_META } from '@/constants/about'

export const AboutMasthead: FC = (): ReactNode => {
    return (
        <>
            <section id='about-intro' className='ed-wrap pt-[clamp(92px,13vh,140px)]'>
                <div className='ed-label' data-reveal='up'>
                    (01) — The practice
                </div>

                <h1 className='ed-display mt-[clamp(18px,3vh,36px)]' data-reveal='words'>
                    <span className='block'>
                        <SplitWords text='A practice' />
                    </span>

                    <span className='block'>
                        <SplitWords text='made of distance' delay={0.09} />
                    </span>

                    <span className='block'>
                        <SplitWords text='and waiting' delay={0.2} />
                    </span>
                </h1>

                <div className='mt-[clamp(22px,3.4vh,44px)] grid gap-y-10 md:grid-cols-12 md:items-start md:gap-x-[clamp(20px,3vw,48px)]'>
                    <div className='md:col-span-5'>
                        <AboutReel />
                    </div>

                    <div className='md:col-span-6 md:col-start-7'>
                        <p className='ed-lead' data-reveal='up'>
                            Morph photographs land at the point where it stops being scenery and starts being geology.
                        </p>

                        <p className='ed-body mt-[clamp(14px,1.8vw,24px)]' data-reveal='up' style={delay(0.08)}>
                            Two of us, working since 2014 out of a yard in Bermondsey. We go back to the same
                            coastlines, the same ice and the same open ground, sometimes for years, until the
                            weather finally does the thing we went there for. Nothing in the archive was found
                            on a first visit.
                        </p>

                        <dl className='mt-[clamp(20px,2.8vh,36px)] grid grid-cols-2 gap-x-8 gap-y-6'>
                            {ABOUT_META.map((item, index) => (
                                <div key={item.label} data-reveal='up' style={delay(index * 0.07)}>
                                    <dt className='ed-mono text-faint'>
                                        {item.label}
                                    </dt>

                                    <dd className='mt-2.5 text-[clamp(15px,1.5vw,21px)] leading-tight tracking-[-0.015em] text-foreground'>
                                        {item.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>

                <div className='ed-rule mt-[clamp(28px,4vh,52px)]' data-reveal='rule' />
            </section>

            <section className='ed-wrap mt-[clamp(32px,5vh,64px)]'>
                <div className='grid gap-y-6 md:grid-cols-12 md:items-end md:gap-x-[clamp(20px,3vw,48px)]'>
                    <div className='md:col-span-3 md:pb-[clamp(8px,2vh,28px)]'>
                        <div className='ed-mono text-faint' data-reveal='up'>
                            Field record
                        </div>

                        <p className='ed-body mt-4' data-reveal='up' style={delay(0.06)}>
                            Two cameras on the same ridge, four hours before anything happened. Most of this
                            work is standing still in the cold with the shutter cable already in your hand.
                        </p>
                    </div>

                    <div className='md:col-span-9'>
                        <Plate
                            className='aspect-[16/9]'
                            src='/images/about/portrait.png'
                            alt='Two cameras on tripods silhouetted against a heavy sky, with a photographer waiting beside them'
                            sizes='(max-width: 767px) 100vw, 75vw'
                            parallax='70'
                        />
                    </div>
                </div>
            </section>
        </>
    )
}
